import { useState } from 'react';
import type { AnalyticsSchemaSnapshotContent } from '@voogzy/contracts';
import { formatDateTime } from '../../shared/date-time';
import type { SchemaRelease, SchemaState } from './schema-editor-types';

export function SchemaReleases(props: {
  state: SchemaState;
  busy: boolean;
  dirty: boolean;
  canManage: boolean;
  onLoadRelease: (revision: number) => Promise<SchemaRelease>;
  onRestoreRelease: (revision: number) => Promise<void>;
}): React.JSX.Element {
  const [selected, setSelected] = useState<SchemaRelease | null>(null);
  const [loading, setLoading] = useState(false);
  const releaseButtons: React.JSX.Element[] = [];
  for (const release of props.state.releases) {
    releaseButtons.push(
      <button className={selected?.revision === release.revision ? 'schema-release-row active' : 'schema-release-row'} data-revision={release.revision} key={release.revision} onClick={selectRelease} type="button">
        <span><strong>Revision {release.revision}</strong><small>{formatDate(release.publishedAt)} · {release.publishedBy ?? 'system seed'}</small></span>
        <code>{release.hash.slice(0, 12)}</code>
      </button>,
    );
  }
  const comparison = selected?.content === undefined || props.state.preview === null
    ? null
    : compareSchemas(selected.content, props.state.preview);

  return (
    <section className="schema-editor-grid schema-release-grid">
      <aside className="surface schema-definition-browser">
        <div className="surface-heading"><div><span className="eyebrow">Immutable history</span><h2>Releases</h2></div><strong>{props.state.releases.length}</strong></div>
        <div className="schema-list">{releaseButtons.length === 0 ? <p className="schema-empty-list">No schema has been published yet.</p> : releaseButtons}</div>
      </aside>

      <article className="surface schema-detail-editor schema-release-detail">
        {selected === null ? <div className="schema-empty-detail">Select a release to inspect, compare, or restore it.</div> : (
          <>
            <div className="surface-heading">
              <div><span className="eyebrow">Published snapshot</span><h2>Revision {selected.revision}</h2></div>
              <code>{selected.hash.slice(0, 16)}</code>
            </div>
            {loading ? <div className="schema-empty-detail">Loading release…</div> : selected.content === undefined ? <div className="schema-empty-detail">Release content could not be loaded.</div> : (
              <>
                <div className="schema-release-metrics">
                  <span><strong>{selected.content.categories.length}</strong>Categories</span>
                  <span><strong>{selected.content.enums.length}</strong>Enums</span>
                  <span><strong>{selected.content.events.length}</strong>Events</span>
                </div>
                {comparison !== null && <div className="schema-release-comparison"><strong>Compared with current valid draft</strong><span>{comparison}</span></div>}
                <div className="schema-release-metadata"><span>Published</span><strong>{formatDate(selected.publishedAt)}</strong><span>Publisher</span><strong>{selected.publishedBy ?? 'system seed'}</strong></div>
                {props.canManage && <div className="schema-editor-actions"><span /><button className="secondary-button" disabled={props.busy || props.dirty} data-revision={selected.revision} onClick={restoreRelease} type="button">Create draft from this release</button></div>}
                {props.dirty && <p className="schema-review-warning">Reset or save current unsaved changes before restoring a release.</p>}
                <details className="schema-release-json"><summary>Read-only JSON</summary><pre>{JSON.stringify(selected.content, null, 2)}</pre></details>
              </>
            )}
          </>
        )}
      </article>
    </section>
  );

  async function selectRelease(event: React.MouseEvent<HTMLButtonElement>): Promise<void> {
    const revision = Number(event.currentTarget.dataset.revision);
    const summary = findRelease(props.state.releases, revision);
    if (summary === undefined) {
      return;
    }
    setSelected(summary);
    setLoading(true);
    try {
      setSelected(await props.onLoadRelease(revision));
    } catch {
      setSelected(summary);
    } finally {
      setLoading(false);
    }
  }

  async function restoreRelease(event: React.MouseEvent<HTMLButtonElement>): Promise<void> {
    const revision = Number(event.currentTarget.dataset.revision);
    if (!window.confirm(`Replace the shared draft with immutable revision ${revision}?`)) {
      return;
    }
    await props.onRestoreRelease(revision);
  }
}

function findRelease(releases: SchemaRelease[], revision: number): SchemaRelease | undefined {
  for (const release of releases) {
    if (release.revision === revision) {
      return release;
    }
  }
  return undefined;
}

function compareSchemas(left: AnalyticsSchemaSnapshotContent, right: AnalyticsSchemaSnapshotContent): string {
  const categoryChanges = compareDefinitionKeys(readCategoryKeys(left), readCategoryKeys(right));
  const enumChanges = compareDefinitionKeys(readEnumKeys(left), readEnumKeys(right));
  const eventChanges = compareDefinitionKeys(readEventKeys(left), readEventKeys(right));
  return `${categoryChanges} category changes · ${enumChanges} enum changes · ${eventChanges} event changes`;
}

function compareDefinitionKeys(left: ReadonlySet<string>, right: ReadonlySet<string>): number {
  let changes = 0;
  for (const key of left) {
    if (!right.has(key)) {
      changes += 1;
    }
  }
  for (const key of right) {
    if (!left.has(key)) {
      changes += 1;
    }
  }
  return changes;
}

function readCategoryKeys(content: AnalyticsSchemaSnapshotContent): Set<string> {
  const values = new Set<string>();
  for (const category of content.categories) {
    values.add(`${category.wireCode}:${category.key}:${category.displayName}:${category.description}`);
  }
  return values;
}

function readEnumKeys(content: AnalyticsSchemaSnapshotContent): Set<string> {
  const values = new Set<string>();
  for (const definition of content.enums) {
    values.add(JSON.stringify(definition));
  }
  return values;
}

function readEventKeys(content: AnalyticsSchemaSnapshotContent): Set<string> {
  const values = new Set<string>();
  for (const event of content.events) {
    values.add(JSON.stringify(event));
  }
  return values;
}

function formatDate(value: string): string { return formatDateTime(value); }
